import React, { CSSProperties, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Flex } from 'components/common/styled/common-styled';
import { ResourceParagraph } from 'components/theme/Typography/ResourceTypography';
import { ITier } from 'features/ShoppingCart/types';
import type { StayQuote } from 'utils/cart/stayQuote';
import { ResourceGroupDetail } from 'features/Reserve/components/ResourceGroupDetail';
import { ResourceGroupFullDetail } from 'features/Reserve/components/ResourceGroupFullDetail';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import { useIsMobile } from 'WindowDimensionProvider';
import { getListingCoverUrl, getListingCoverAltText } from 'utils/listings';
import CancellationPolicySummary from 'features/ShoppingCart/components/CancellationPolicySummary';
import { ResourceGroupV3 } from '@kouto/types';
import PriceTiersSummary from './PriceTiersSummary';

interface ResourceGroupSummaryProps {
  /** Fill the column instead of standing at sidebar width, for a page with one narrow column. */
  fullWidth?: boolean;
  priceTiers: Array<ITier>;
  resourceGroup: ResourceGroupV3;
  stayQuote?: StayQuote | null;
}

const ResourceGroupSummary = ({
  fullWidth = false,
  priceTiers,
  resourceGroup,
  stayQuote = null,
}: ResourceGroupSummaryProps) => {
  const isMobile = useIsMobile();
  const [showDetails, setShowDetails] = useState(false);
  const { listing, cancellationPolicy } = useResourceBookingSession();
  const isMapped = useMemo(() => listing?.isMapped ?? false, [listing]);
  const { t: translate } = useTranslation();

  const coverImageUrl = useMemo(
    () => (listing ? getListingCoverUrl(listing, isMobile) : ''),
    [listing, isMobile],
  );

  const coverAltText = useMemo(
    () => (listing ? getListingCoverAltText(listing) : null),
    [listing],
  );

  if (!listing) {
    return null;
  }

  return (
    <>
      <SidebarWrapper
        direction="column"
        alignItem="flex-start"
        gap={24}
        data-testid="resource-group-summary"
        style={fullWidth ? fullWidthStyle : undefined}
      >
        <PriceTiersSummary
          priceTiers={priceTiers}
          stayQuote={stayQuote}
          title={resourceGroup.title ?? 'Title not available'}
          taxes={resourceGroup?.experiences?.[0]?.taxes ?? []}
          coverImageUrl={coverImageUrl}
          coverAltText={coverAltText}
          applyFlatFeeToEachTier={!isMapped}
        />
        <DetailsWrapper
          className="summary-wrapper resource-group-summary__details-wrapper"
          data-testid="resource-group-summary-details"
          style={fullWidth ? fullWidthDetailsStyle : undefined}
        >
          <Flex direction="column" gap={12} alignItem="flex-start">
            <ResourceParagraph
              className="resource-group-summary__total"
              data-testid="resource-group-summary-total"
              color="var(--way-palette-black-100)"
              fontSize="14px"
              fontWeight="700"
            >
              {translate('details')}
            </ResourceParagraph>

            <ResourceGroupDetail
              description={
                resourceGroup.description ?? 'Description not available'
              }
              maxLines={2}
              customStyles={textStyles}
              onReadMore={() => setShowDetails(true)}
              hasMoreToShow={
                (!!cancellationPolicy && cancellationPolicy.active) ||
                !!resourceGroup.included.length
              }
            />
          </Flex>
        </DetailsWrapper>
        <CancellationPolicySummary cancellationPolicy={cancellationPolicy} />
      </SidebarWrapper>
      <ResourceGroupFullDetail
        description={resourceGroup.description ?? 'Description not available'}
        includedItems={resourceGroup.included}
        showContent={showDetails}
        onClose={() => setShowDetails(false)}
        maxLines={300}
        cancellationPolicyText={
          cancellationPolicy?.active ? cancellationPolicy.notes : undefined
        }
        capacityCount={Number(
          resourceGroup.experiences?.[0]?.displayCapacity ?? 0,
        )}
      />
    </>
  );
};

const fullWidthStyle: CSSProperties = { width: '100%' };
const fullWidthDetailsStyle: CSSProperties = { maxWidth: 'none' };

const SidebarWrapper = styled(Flex)`
  width: 350px;

  & > * {
    width: 100%;
  }
`;

const WrapperDiv = styled.div`
  padding: 24px;
`;

const DetailsWrapper = styled(WrapperDiv)`
  border: solid 0.5px var(--way-colors-borderColor);
  max-width: 350px;
`;

const textStyles = css`
  margin-bottom: 0;
  & > p {
    font-family: ${({ theme }) => theme.font.primaryFont};
    margin: 0px;
    padding: 0px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.4;
    font-size: 14px;
    color: var(--way-colors-primaryTextColor);
  }
`;

export default ResourceGroupSummary;
